/**
 * Human-readable explanations for outreach dropouts (Potential Analyzer,
 * no email, research gates, …) so the job funnel can show what happened.
 */

const FAIL_COPY = {
  no_email: "No recipient email on this contact.",
  no_decision_maker_email: "Decision-maker email was required and none was found.",
  no_email_found: "DM Email Finder did not find an address.",
  no_business_or_website: "No company name or website to analyse.",
  no_website_or_name: "No website or name for web research.",
  no_instagram_handle: "No Instagram handle on this contact.",
  research_failed: "Web research returned nothing usable.",
  ig_research_failed: "Instagram research failed.",
  place_not_found: "Business not found on Google Maps.",
  place_unavailable: "Google Maps did not return the business.",
  google_throttled: "Google kept throttling the review audit.",
  no_business_name: "No company name or website for the review audit.",
  review_audit_error: "Google Review Audit hit an error.",
  connector_no_match: "The custom researcher found nothing for this contact.",
  connector_missing_input: "A field the custom researcher needs (e.g. city) is empty on this contact.",
  data_connector_unavailable: "The custom researcher's platform stayed unreachable.",
  data_connector_error: "The custom researcher hit an error.",
  filtered_out: "Cleanup rules removed this contact.",
  opted_out: "Contact has opted out.",
  blacklisted: "Contact or domain is on the blacklist.",
  undeliverable: "Email was marked undeliverable.",
  contact_missing: "Contact was deleted from the database.",
  contact_deleted: "Contact was deleted from the database.",
  recipient_invalid: "Recipient email did not pass validation.",
  recipient_invalid_no_fallback: "Recipient email was invalid and no fallback was allowed.",
  empty_body: "Generated email was empty.",
  potential_error: "Potential Analyzer hit a temporary error (retried).",
  status_mismatch: "Contact no longer matches this agent's audience.",
  tag_removed: "The required tag is no longer on this contact.",
  safeguard_rejected: "SafeGuard rejected this email.",
  max_retries: "This step failed too many times.",
  manually_removed: "Removed from the agent by hand.",
  call_notes_missing: "The template needs call notes and none were logged since the last email.",
  call_not_interested: "The last logged call says the contact is not interested.",
};

const THRESHOLD_RE = /potential_below_threshold\s*\((\d+(?:\.\d+)?)\s*<\s*(\d+(?:\.\d+)?)\)/i;

export const parsePotentialThreshold = (reason) => {
  const m = String(reason || "").match(THRESHOLD_RE);
  if (!m) return null;
  return { score: Number(m[1]), minScore: Number(m[2]) };
};

export const failReasonKey = (reason) => {
  const raw = String(reason || "").trim();
  if (!raw) return "";
  const afterStep = raw.includes(":") ? raw.slice(raw.indexOf(":") + 1).trim() : raw;
  return afterStep.split(/[\s(]/)[0] || raw;
};

export const humanizeFailReason = (reason) => {
  const raw = String(reason || "").trim();
  if (!raw) return "Left the sequence.";
  const threshold = parsePotentialThreshold(raw);
  if (threshold) {
    return `Potential score ${threshold.score}/10 was below the ${threshold.minScore}/10 gate.`;
  }
  const key = failReasonKey(raw);
  if (FAIL_COPY[key]) return FAIL_COPY[key];
  const step = raw.includes(":") ? raw.slice(0, raw.indexOf(":")).replace(/_/g, " ") : "";
  const rest = (FAIL_COPY[key] || key.replace(/_/g, " ")).replace(/_/g, " ");
  return step ? `${step}: ${rest}` : rest;
};

export const failStepLabel = (reason) => {
  const raw = String(reason || "");
  if (!raw.includes(":")) return "";
  return raw.slice(0, raw.indexOf(":")).replace(/_/g, " ");
};

export const compactFailAnalysis = (analysis = {}, extras = {}) => {
  const bullets = Array.isArray(analysis.bullets)
    ? analysis.bullets.filter((b) => typeof b === "string" && b.trim()).slice(0, 6)
    : [];
  return {
    score: extras.score != null ? extras.score : analysis.score,
    minScore: extras.minScore != null ? extras.minScore : analysis.minScore,
    fitLabel: analysis.fitLabel || "",
    summary: String(analysis.summary || "").slice(0, 600),
    bullets,
    sizeAssessment: analysis.sizeAssessment || "",
    responseChance: analysis.responseChance || null,
    dataConfidence: analysis.dataConfidence || "",
    analyzedAt: analysis.analyzedAt || "",
  };
};
